import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AutoMode as AutoModeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';

type ReminderPath = 'elearning' | 'blended';

type TemplateOption = {
  id: string;
  key: string;
  name: string;
  description: string;
  isAutomaticReminder: boolean;
};

type TriggerRow = {
  key: string;
  path: ReminderPath;
  label: string;
  description: string;
  templateId: string | null;
  defaultTemplateKey: string;
  sortOrder: number;
};

type GlobalDefaultsResponse = {
  includeMagicLink: boolean;
  customButtonUrl: string;
  triggers: TriggerRow[];
  templates: TemplateOption[];
};

const ReminderGlobalDefaults: React.FC = () => {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<GlobalDefaultsResponse | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch(`${apiBaseUrl}/reminder-global-defaults`);
      if (!response.ok) throw new Error('Configuration introuvable');
      setConfig((await response.json()) as GlobalDefaultsResponse);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const templatesById = useMemo(() => {
    const map = new Map<string, TemplateOption>();
    config?.templates.forEach((t) => map.set(t.id, t));
    return map;
  }, [config?.templates]);

  const elearningTriggers = useMemo(
    () => config?.triggers.filter((t) => t.path === 'elearning') || [],
    [config?.triggers]
  );

  const blendedTriggers = useMemo(
    () => config?.triggers.filter((t) => t.path === 'blended') || [],
    [config?.triggers]
  );

  const updateTrigger = (key: string, templateId: string | null) => {
    if (!config) return;
    setConfig({
      ...config,
      triggers: config.triggers.map((row) =>
        row.key === key ? { ...row, templateId } : row
      ),
    });
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await adminFetch(`${apiBaseUrl}/reminder-global-defaults`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          includeMagicLink: config.includeMagicLink,
          customButtonUrl: config.customButtonUrl,
          triggers: config.triggers.map((t) => ({
            key: t.key,
            templateId: t.templateId,
          })),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'Erreur lors de la sauvegarde');
      }

      setConfig((await response.json()) as GlobalDefaultsResponse);
      setSuccess('Configuration enregistrée.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const renderTriggerCard = (trigger: TriggerRow) => (
    <Card key={trigger.key}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="subtitle1">{trigger.label}</Typography>
              <Chip size="small" label={trigger.key} variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {trigger.description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Template système : {trigger.defaultTemplateKey}
            </Typography>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel id={`global-template-${trigger.key}`}>Template par défaut</InputLabel>
            <Select
              labelId={`global-template-${trigger.key}`}
              label="Template par défaut"
              value={trigger.templateId || ''}
              onChange={(e) =>
                updateTrigger(trigger.key, e.target.value ? String(e.target.value) : null)
              }
            >
              <MenuItem value="">
                Template système ({trigger.defaultTemplateKey})
              </MenuItem>
              {config?.templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                  {template.isAutomaticReminder ? ' [Relance auto]' : ''} ({template.key})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {trigger.templateId && templatesById.get(trigger.templateId) && (
            <Typography variant="caption" color="text.secondary">
              {templatesById.get(trigger.templateId)?.description}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Configuration introuvable'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student-messages')}>
          Retour
        </Button>
        <AutoModeIcon color="primary" />
        <Box>
          <Typography variant="h5">Relances automatiques — paramètres globaux</Typography>
          <Typography variant="body2" color="text.secondary">
            Templates appliqués lors de la première activation sur une formation. Les formations
            déjà configurées ne sont pas modifiées.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.includeMagicLink}
                  onChange={(e) => setConfig({ ...config, includeMagicLink: e.target.checked })}
                />
              }
              label="Inclure un magic link par défaut (première activation formation)"
            />

            {!config.includeMagicLink && (
              <TextField
                label="URL du bouton par défaut"
                value={config.customButtonUrl}
                onChange={(e) => setConfig({ ...config, customButtonUrl: e.target.value })}
                fullWidth
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        E-learning
      </Typography>
      <Stack spacing={2} mb={4}>
        {elearningTriggers.map(renderTriggerCard)}
      </Stack>

      <Typography variant="h6" gutterBottom>
        Blended
      </Typography>
      <Stack spacing={2} mb={4}>
        {blendedTriggers.map(renderTriggerCard)}
      </Stack>

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Enregistrer
        </Button>
      </Box>
    </Container>
  );
};

export default ReminderGlobalDefaults;
